import {
  ENTITY_ACCESS_RIGHTS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
} from "constants/constants";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  createStaffQuery,
  deleteStaffQuery,
  getStaffByArrayIdQuery,
  getStaffByIdQuery,
  getStaffBySlugQuery,
  searchStaffQuery,
  updateStaffQuery,
  getStaffByRoleQuery,
  getStaffByRoleAndNameQuery,
  getStaffFourYearsReviewQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { setListUniqByIdHelper, withUserScopeCheck } from "utils/helper";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const StaffContext = createContext({});

export const useStaffContext = () => useContext(StaffContext);

export const StaffProvider = ({ ...props }) => {
  const { apiErrorResponse, setApiErrorResponse } = useUserContext();
  const [staffList, setStaffList] = useState([]);
  const [staffById, setStaffById] = useState({});
  const [staffByIds, setStaffByIds] = useState([]);
  const [staffBySlug, setStaffBySlug] = useState({});
  const lastStaffSlugRef = useRef(null);
  const lastFourYearsReviewIdRef = useRef(null);
  const [createdStaff, setCreatedStaff] = useState({});
  const [createStaffStatus, setCreateStaffStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [editStaffStatus, setEditStaffStatus] = useState(QUERY_STATUS.INITIAL);
  const [deleteStaffStatus, setDeleteStaffStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [displayStaffStatus, setDisplayStaffStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [refresh, setRefresh] = useState(false);
  const [staffAccessRights, setStaffAccessRights] = useState([]);
  const [staffFourYearsReview, setStaffFourYearsReview] = useState([]);

  const { userScope } = useUserContext();

  const getStaffByRole = useCallback(
    async (role) => {
      return await withUserScopeCheck({
        callback: async () => {
          const result = await getStaffByRoleQuery({ role, userScope });
          setStaffList(result);
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const searchStaffByRoleAndName = useCallback(
    async ({ role, search }) => {
      return await withUserScopeCheck({
        callback: async () => {
          const result = await getStaffByRoleAndNameQuery({
            role,
            search,
            userScope,
          });
          setStaffList((old) => setListUniqByIdHelper(old, result));
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const getStaffBySlug = useCallback(
    async (slug) => {
      if (slug !== lastStaffSlugRef.current) {
        setDisplayStaffStatus(QUERY_STATUS.LOADING);
        lastStaffSlugRef.current = slug;
      }
      setStaffAccessRights([]);
      return await withUserScopeCheck({
        callback: async () => {
          try {
            const result = await getStaffBySlugQuery({ slug, userScope });
            const hasGetAccessRights = result?.access?.includes(
              ENTITY_ACCESS_RIGHTS.GET
            );
            if (hasGetAccessRights) {
              setStaffBySlug({ ...result.contract, ...result.staff });
              setStaffAccessRights(result.access);
              setDisplayStaffStatus(QUERY_STATUS.SUCCESS);
            } else {
              setDisplayStaffStatus(QUERY_STATUS.ERROR);
            }
          } catch (error) {
            if (error.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
              setDisplayStaffStatus(QUERY_STATUS.NOT_FOUND);
            } else {
              setDisplayStaffStatus(QUERY_STATUS.ERROR);
            }
          }
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const getStaffById = useCallback(
    async (id) => {
      return await withUserScopeCheck({
        callback: async () => {
          const result = await getStaffByIdQuery({ id, userScope });
          setStaffById({ ...result.contract, ...result.staff });
          setDisplayStaffStatus(QUERY_STATUS.SUCCESS);
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const getStaffByArrayId = useCallback(
    async (ids) => {
      return await withUserScopeCheck({
        callback: async () => {
          const result = await getStaffByArrayIdQuery({ ids, userScope });
          setStaffByIds(result);
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const createStaff = useCallback(
    async (data) => {
      setCreateStaffStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        const result = await createStaffQuery({ data, userScope });
        if (result) {
          setCreateStaffStatus(QUERY_STATUS.SUCCESS);
          return setCreatedStaff(result.staff);
        }
      } catch (error) {
        console.error(
          "Staff creation error:",
          error?.response?.data?.["bad-request"]
        );
        setCreateStaffStatus(QUERY_STATUS.ERROR);
        setApiErrorResponse(
          extractApiFieldErrorsFlat(error?.response?.data?.["bad-request"])
        );
      }
    },
    [userScope]
  );

  const updateStaff = useCallback(
    async (id, data) => {
      setEditStaffStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        await withUserScopeCheck({
          callback: async () => {
            const updatedStaff = await updateStaffQuery({
              id,
              data,
              userScope,
            });
            setStaffBySlug({ ...updatedStaff.contract, ...updatedStaff.staff });

            const connectedUserId =
              Number(localStorage.getItem("connected_as")) ||
              Number(localStorage.getItem("id"));
            if (updatedStaff.staff?.id === connectedUserId) {
              setStaffById({ ...updatedStaff.contract, ...updatedStaff.staff });
            }
            setEditStaffStatus(QUERY_STATUS.SUCCESS);
            setRefresh(true);
          },
          scope: userScope,
        });
      } catch (error) {
        console.error(
          "Staff update error:",
          error?.response?.data?.["bad-request"]
        );
        setEditStaffStatus(QUERY_STATUS.ERROR);
        setApiErrorResponse(
          extractApiFieldErrorsFlat(error?.response?.data?.["bad-request"])
        );
      }
    },
    [userScope]
  );

  const deleteStaff = useCallback(
    async (id) => {
      setDeleteStaffStatus(QUERY_STATUS.LOADING);
      try {
        await withUserScopeCheck({
          callback: async () => {
            await deleteStaffQuery({ id, userScope });
          },
          scope: userScope,
        });
        setDeleteStaffStatus(QUERY_STATUS.SUCCESS);
        setStaffById({});
        setStaffBySlug({});
      } catch (error) {
        setDeleteStaffStatus(QUERY_STATUS.ERROR);
        console.error("deleteStaffError:", error);
      }
    },
    [userScope]
  );

  const staffListGetter = useCallback(
    async (val) => {
      return await withUserScopeCheck({
        callback: async () => {
          const data = await searchStaffQuery({ val, userScope });
          setStaffList(data);
          return data;
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const getStaffFourYearsReview = useCallback(
    async ({ id }) => {
      // The page stays mounted when switching profiles: drop the previous
      // profile's reviews and ignore responses that arrive after a switch.
      if (id !== lastFourYearsReviewIdRef.current) {
        setStaffFourYearsReview([]);
        lastFourYearsReviewIdRef.current = id;
      }
      try {
        const result = await getStaffFourYearsReviewQuery({ id, userScope });
        if (id === lastFourYearsReviewIdRef.current) {
          setStaffFourYearsReview(result);
        }
      } catch (e) {
        console.error("getStaffFourYearsReview error:", e);
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      userScope,
      searchStaffByRoleAndName,
      getStaffByRole,
      refresh,
      setRefresh,
      createStaffStatus,
      createdStaff,
      editStaffStatus,
      getStaffByArrayId,
      setCreateStaffStatus,
      setCreatedStaff,
      setEditStaffStatus,
      setStaffById,
      staffById,
      getStaffById,
      staffByIds,
      createStaff,
      staffList,
      staffListGetter,
      updateStaff,
      deleteStaff,
      displayStaffStatus,
      getStaffBySlug,
      staffBySlug,
      setStaffBySlug,
      setStaffAccessRights,
      staffAccessRights,
      apiErrorResponse,
      deleteStaffStatus,
      setDeleteStaffStatus,
      staffFourYearsReview,
      getStaffFourYearsReview,
    }),
    [
      refresh,
      setRefresh,
      createStaffStatus,
      createdStaff,
      editStaffStatus,
      staffById,
      staffByIds,
      staffList,
      displayStaffStatus,
      staffBySlug,
      userScope,
      setStaffAccessRights,
      staffAccessRights,
      apiErrorResponse,
      setApiErrorResponse,
      deleteStaffStatus,
      setDeleteStaffStatus,
      staffFourYearsReview,
      getStaffFourYearsReview,
    ]
  );
  return <StaffContext.Provider value={values} {...props} />;
};
